import { createElement, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { post, put } from "../../common/api/apiShared";
import { Result } from "../../common/api/result";
import { validateEmail } from "../../common/emailValidator";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { useTranslation } from "../../localization/useTranslation";
import { SelectableColoredItem, SelectableItem } from "../../processingActivity/ProcessingActivity.types";
import { getStartPageName, LoginProvider, SelectableUserRoleItem, UserSaveModel, UserStartPage, UserViewModel } from "../User.types";
import { IAddEditUserDialogProps } from "./AddEditUserDialog";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useGroupCompaniesQuery } from "../../common/hooks/useSelectableItemQueries";
import { getEnumValues } from "../../common/enumOperations";
import { exhaustiveGuard } from "../../common/utilities";
import { X } from "@mui/icons-material";
import { useDotLegalQuery, useDotLegalMutation, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useStartPage } from "../hooks/useStartPage";

export function useAddEditUserDialogHook(props: IAddEditUserDialogProps) {
    const { loginProvider } = useUserContext();
    const { isStartPageShownInMenu } = useStartPage();

    const [viewModel, setViewModel] = useState<UserSaveModel>({
        firstName: "",
        lastName: "",
        emailAddress: "",
        businessAreas: [],
        userRoles: [],
        gdpo: false,
        integrationIdentifier: "",
        groupEntityIds: [],
        startPage: undefined,
    });
    const [runValidation, setRunValidation] = useState(false);
    const [useSameIntegrationIdentifierAsEmail, setUseSameIntegrationIdentifierAsEmail] = useState(!props.isEdit);

    function getEmptyValidationObject() {
        return {
            firstNameError: "",
            lastNameError: "",
            emailError: "",
            integrationIdentifierError: "",
            groupEntitiesError: "",
            startPageError: "",
        };
    }

    const [errors, setErrors] = useState(getEmptyValidationObject());
    const { permissions } = useUserContext();
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();

    let { isLoading, data } = useDotLegalQueryWithKey<UserViewModel>("addUser" + props.userId, `/user/${props.userId}`, {
        enabled: props.userId !== undefined,
    });
    let userRolesQuery = useDotLegalQueryWithKey<Array<SelectableUserRoleItem>>("userRoles", "/userroles/selectableItems");
    let { data: businessAreaOptions, isLoading: businessAreaIsLoading } = useDotLegalQueryWithKey<Array<SelectableColoredItem>>(
        "selectableBusinessAreas",
        "/BusinessAreas/businessareas"
    );
    let groupEntitiesQuery = useGroupCompaniesQuery();

    useEffect(() => {
        if (data) {
            setViewModel({
                firstName: data.firstName ?? "",
                lastName: data.lastName ?? "",
                emailAddress: data.emailAddress ?? "",
                businessAreas: data.businessAreas.map((businessArea) => businessArea.id) ?? [],
                userRoles: data.userRoles.map((role) => role.id) ?? [],
                gdpo: data.gdpo ?? false,
                integrationIdentifier: data.integrationIdentifier,
                groupEntityIds: data.userGroupEntities.map((g) => g.id),
                startPage: data.startPage ?? UserStartPage.Dashboard,
            });
        }
    }, [props.userId, data]);

    const saveMutation = useDotLegalMutation(save);
    const createUser = () => {
        saveMutation.mutateAsync(viewModel!, {
            onSuccess: (resp: Result<string>) => {
                if (resp.hasErrors()) {
                    snackbar.show(translateString("userExistWithEmail"), "warning");
                } else {
                    snackbar.show(
                        createElement(Trans, { i18nKey: "createdSuccessGeneric", values: { name: viewModel?.firstName + " " + viewModel?.lastName } })
                    );
                    props.onSaveCorrect();
                }
            },
        });
    };

    const editMutation = useDotLegalMutation(edit);
    const updateUser = () => {
        editMutation.mutateAsync(viewModel!, {
            onSuccess: (resp: any) => {
                const userEditReponst = resp.value();
                if (userEditReponst.success) {
                    snackbar.show(createElement(Trans, { i18nKey: "userEditSuccess", values: { name: userEditReponst.name } }));
                    props.onSaveCorrect();
                } else {
                    snackbar.show(resp.value().error, "warning");
                }
            },
        });
    };

    const onChange = (model: UserSaveModel) => {
        setViewModel(model);
        validateForm(model, runValidation);
    };

    const onSave = () => {
        setRunValidation(true);
        if (validateForm(viewModel, true)) {
            if (props.isEdit) updateUser();
            else createUser();
        }
    };

    function save(userModel: UserSaveModel) {
        return post<string>("/user", userModel);
    }

    function edit(viewModel: UserSaveModel) {
        return put<{}>(`/user/${props.userId}`, viewModel);
    }

    function validateForm(viewModel: UserSaveModel | undefined, runValidation: boolean) {
        if (!runValidation) {
            return true;
        }
        let formValid = true;
        let tempErrors = getEmptyValidationObject();

        if (isNullOrWhitespace(viewModel?.firstName)) {
            tempErrors.firstNameError = translateString("fieldRequired");
            formValid = false;
        }

        if (isNullOrWhitespace(viewModel?.lastName)) {
            tempErrors.lastNameError = translateString("fieldRequired");
            formValid = false;
        }

        if (!viewModel?.startPage) {
            tempErrors.startPageError = translateString("fieldRequired");
            formValid = false;
        }

        if (isNullOrWhitespace(viewModel?.emailAddress) || !validateEmail(viewModel!.emailAddress)) {
            tempErrors.emailError = translateString("emailInvalid");
            formValid = false;
        }

        if (!useSameIntegrationIdentifierAsEmail) {
            if (loginProvider === LoginProvider.AzureAd || permissions.canManageCustomers) {
                if (isNullOrWhitespace(viewModel?.integrationIdentifier)) {
                    tempErrors.integrationIdentifierError = translateString("fieldRequired");
                    formValid = false;
                } else if (!validateEmail(viewModel!.integrationIdentifier)) {
                    tempErrors.integrationIdentifierError = translateString("emailInvalid");
                    formValid = false;
                }
            }
        }

        if (
            !viewModel?.gdpo &&
            groupEntitiesQuery.data &&
            groupEntitiesQuery.data.length > 1 &&
            (!viewModel?.groupEntityIds || viewModel?.groupEntityIds.length === 0)
        ) {
            tempErrors.groupEntitiesError = translateString("fieldRequired");
            formValid = false;
        }

        setErrors(tempErrors);
        return formValid;
    }

    const getAvaliableStartPages = (selectedUserRoles: Array<string>) => {
        return [
            ...new Set(
                userRolesQuery.data
                    ?.filter((x) => selectedUserRoles.includes(x.id))
                    .map((x) => x.startPages)
                    .flat() ?? []
            ),
        ].filter(isStartPageShownInMenu);
    };

    const getStartPageOptions = () => {
        let options: Array<SelectableItem> = [];

        getAvaliableStartPages(viewModel.userRoles).forEach((startPage) => {
            options.push({ id: startPage.toString(), name: getStartPageName(startPage, translateString) });
        });

        return options;
    };

    const getBusinessAreaOptions = (): Array<SelectableColoredItem> | undefined => {
        if (!businessAreaOptions) {
            return undefined;
        }

        const hasUnselectedOptions = businessAreaOptions.some((ba) => !viewModel.businessAreas.includes(ba.id));

        if (!hasUnselectedOptions) {
            return businessAreaOptions;
        }

        return [{ id: "All", name: translateString("addAll"), color: undefined }, ...businessAreaOptions];
    };

    const onBusinessAreasChange = (selectedBusinessAreas: Array<string>) => {
        let tempViewModel = { ...viewModel };

        if (selectedBusinessAreas.includes("All") && businessAreaOptions) {
            tempViewModel.businessAreas = businessAreaOptions.map((ba) => ba.id);
        } else {
            tempViewModel.businessAreas = selectedBusinessAreas.filter((id) => id !== "All");
        }

        onChange(tempViewModel);
    };

    // The multi-select hands renderOptions a DotLegalSelectOption, which carries only id and name, so the flag is
    // looked up by id rather than read off the option.
    const fullAccessRoleIds = new Set(userRolesQuery.data?.filter((x) => x.hasFullAccess).map((x) => x.id) ?? []);

    return {
        userRolesLoading: userRolesQuery.isLoading,
        userRolesData: userRolesQuery.data,
        fullAccessRoleIds,
        businessAreasLoading: businessAreaIsLoading,
        businessAreaData: getBusinessAreaOptions(),
        groupEntitiesLoading: groupEntitiesQuery.isLoading,
        groupEntitiesData: groupEntitiesQuery.data,
        isLoading,
        viewModel,
        onChange,
        errors,
        isSaving: saveMutation.isLoading || editMutation.isLoading,
        permissions,
        onSave,
        useSameIntegrationIdentifierAsEmail,
        setUseSameIntegrationIdentifierAsEmail,
        startPageOptions: getStartPageOptions(),
        getAvaliableStartPages,
        onBusinessAreasChange,
    };
}
